import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { IDataAlertBase, IDataAlertInfo } from '../models/IDataAlertInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataAlerts } from '../resources/DataAlerts';
import { IResult } from '../ServerApi/types/IResponse';
import { IUserName } from '../ServerApi/types/IUser';
import { ActionHelpers } from './ActionHelpers';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { IErrorDetail } from './ContentActionNotification';
import { IConfirmChangeOwnerScope } from './ContentActions';
import { SearchSuggestionsService } from './SearchSuggestionsService';

export class ChangeDataAlertOwnerAction {

  public static $inject = ['ActionHelpers', 'ConfirmActionDialog', '$translate', 'DataAlerts', 'SearchSuggestionsService'];

  constructor(
    private ActionHelpers: ActionHelpers,
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private DataAlerts: DataAlerts,
    private suggestions: SearchSuggestionsService) { }

  public confirmChangeOwner(dataAlerts: Set<IDataAlertInfo>, context: IActionContext): ng.IPromise<IResult> {

    const ownerPicker = {
      prompt: dataAlerts.size() === 1
          ? this.$translate.instant('changeOwnerDataAlertPrompt', { NAME: L10n.elideIfLong(dataAlerts.first().name) })
          : this.$translate.instant('changeOwnerDataAlertsPrompt', { COUNT: dataAlerts.size() }, L10n.Interpolations.Count),
      ownerQuery: (matchText: string) => this.suggestions.usersWithoutGuest(matchText),
      targetOwner: <IUserName>null
    };
    const newDialogScope = <IConfirmChangeOwnerScope> context.scope.$new();
    newDialogScope.ownerPicker = ownerPicker;

    return this.ConfirmActionDialog.open({
      scope: newDialogScope,
      title: this.$translate.instant('changeOwner'),
      bodyTemplateUrl: 'changeOwnerDialog.html',
      actionName: this.$translate.instant('changeOwnerConfirm', { COUNT: dataAlerts.size() }, L10n.Interpolations.Count),

      action: (dialogInstance: IConfirmActionDialogInstance) => {
        return this.DataAlerts.setOwner(dataAlerts.keys(), ownerPicker.targetOwner.id)
          .then((result: IResult) => this.notifyResult(dataAlerts, result, ownerPicker.targetOwner.displayName));
      },
      confirmDisabled: () => !ownerPicker.targetOwner || !ownerPicker.targetOwner.id
    }).result.finally(() => newDialogScope.$destroy());
  }

  private notifyResult(dataAlerts: Set<IDataAlertInfo>, result: IResult, ownerName: string) {
    this.ActionHelpers.notifyStandardError(
      dataAlerts,
      {
        successId: 'changeOwnerDataAlertsSuccess',
        errorId: 'changeOwnerDataAlertsError'
      },
      (errorItem: IErrorItem<IDataAlertInfo>) => this.errorDetailFor(errorItem),
      { OWNER_NAME: ownerName }
    )(result);
  }

  public errorDetailFor(errorItem: IErrorItem<IDataAlertBase>): IErrorDetail {
    const itemName = errorItem.item.name;
    let errorMessage: string;

    switch (errorItem.error.code) {
      case CommonErrorCode.DataAlertNotFound:
        errorMessage = this.$translate.instant('errorDataAlertNotFound', { ALERT_NAME: L10n.elideIfLong(itemName) });
        break;
      case CommonErrorCode.CallingUserInsufficientPermissions:
        errorMessage = this.$translate.instant('errorDataAlertInsufficientPermissions', { ALERT_NAME: L10n.elideIfLong(itemName) });
        break;
      case CommonErrorCode.SiteUserNotFound:
        errorMessage = this.$translate.instant('errorUserNotFound', { ALERT_NAME: L10n.elideIfLong(itemName) });
        break;
      default:
        errorMessage = this.$translate.instant('errorUnknown');
    }

    return { itemName, errorMessage };
  }
}

Module.instance().service('ChangeDataAlertOwnerAction', ChangeDataAlertOwnerAction);
